import * as React from 'react';
import {
  ColumnDef,
  PaginationState,
  RowSelectionState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTablePagination, showsPagination } from '@/components/ui/data-table-pagination';
import { TABLE_DEFAULT_PAGE_SIZE, TABLE_MIN_WIDTH } from '@constants/table';
import { cn } from '@/lib/utils';

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  loading?: boolean;
  /** Rendered in place of the table body when there is nothing to show. */
  empty?: React.ReactNode;
  onRowClick?: (row: TData) => void;
  getRowId?: (row: TData, index: number) => string;
  sorting?: SortingState;
  /** Server owns row order; headers only report the requested sort. */
  manualSorting?: boolean;
  onSortingChange?: (sorting: SortingState) => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  /** Sentinel row shown under the table while more pages are loading. */
  loadingMore?: boolean;
  /** Announced while `loading`; a bare skeleton tells a screen reader nothing. */
  loadingLabel?: string;
  footer?: React.ReactNode;
  className?: string;
  /** Turns on paging. Omit for a table that shows everything it is given. */
  paginated?: boolean;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  /**
   * Total rows on the server. Supplying it switches the table to manual
   * pagination — TanStack stops slicing `data`, because the caller is already
   * sending one page at a time.
   */
  totalRows?: number;
}

const SORT_ICON = {
  asc: ArrowUp,
  desc: ArrowDown,
  none: ChevronsUpDown,
} as const;

function DataTableComponent<TData>({
  columns,
  data,
  loading = false,
  empty,
  onRowClick,
  getRowId,
  sorting,
  manualSorting = false,
  onSortingChange,
  rowSelection,
  onRowSelectionChange,
  loadingMore = false,
  loadingLabel,
  footer,
  className,
  paginated = false,
  pagination,
  onPaginationChange,
  totalRows,
}: DataTableProps<TData>) {
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const [internalSelection, setInternalSelection] = React.useState<RowSelectionState>({});
  const [internalPagination, setInternalPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: TABLE_DEFAULT_PAGE_SIZE,
  });

  const isSortingControlled = sorting !== undefined;
  const isSelectionControlled = rowSelection !== undefined;
  const isPaginationControlled = pagination !== undefined;
  const activePagination = isPaginationControlled ? pagination : internalPagination;
  const isServerPaginated = totalRows !== undefined;

  // A refresh that returns fewer rows leaves pageIndex pointing past the end,
  // which renders an empty body. Derived rather than corrected in an effect so
  // there is never a frame showing the blank page.
  const safePagination = React.useMemo(() => {
    if (!paginated || isServerPaginated || activePagination.pageSize <= 0) return activePagination;
    const lastPage = Math.max(0, Math.ceil(data.length / activePagination.pageSize) - 1);
    return activePagination.pageIndex > lastPage
      ? { ...activePagination, pageIndex: lastPage }
      : activePagination;
  }, [paginated, isServerPaginated, activePagination, data.length]);

  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: {
      sorting: isSortingControlled ? sorting : internalSorting,
      rowSelection: isSelectionControlled ? rowSelection : internalSelection,
      ...(paginated ? { pagination: safePagination } : {}),
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(isSortingControlled ? sorting : internalSorting) : updater;
      (isSortingControlled ? onSortingChange : setInternalSorting)?.(next);
    },
    onRowSelectionChange: (updater) => {
      const current = isSelectionControlled ? rowSelection : internalSelection;
      const next = typeof updater === 'function' ? updater(current) : updater;
      (isSelectionControlled ? onRowSelectionChange : setInternalSelection)?.(next);
    },
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(safePagination) : updater;
      (isPaginationControlled ? onPaginationChange : setInternalPagination)?.(next);
    },
    enableRowSelection: isSelectionControlled || undefined,
    manualPagination: isServerPaginated,
    manualSorting,
    ...(manualSorting ? { enableSortingRemoval: false, enableMultiSort: false } : {}),
    rowCount: totalRows,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(paginated && !isServerPaginated
      ? { getPaginationRowModel: getPaginationRowModel() }
      : {}),
  });

  const columnCount = table.getAllLeafColumns().length;
  const rows = table.getRowModel().rows;

  return (
    <div
      className={cn('@container/table overflow-hidden rounded-xl border bg-card', className)}
      aria-busy={loading}
    >
      {loading && (
        <span role="progressbar" aria-label={loadingLabel} className="sr-only">
          {loadingLabel}
        </span>
      )}
      <div className="overflow-x-auto">
        <Table className={TABLE_MIN_WIDTH}>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id} className="hover:bg-transparent">
                {group.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const direction = header.column.getIsSorted() || 'none';
                  const Icon = SORT_ICON[direction as keyof typeof SORT_ICON];

                  return (
                    <TableHead
                      key={header.id}
                      aria-sort={canSort ? (direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none') : undefined}
                      className="whitespace-nowrap"
                      style={{ width: header.getSize() || undefined }}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="-ml-1 inline-flex items-center gap-1 rounded-sm px-1 py-0.5 outline-hidden transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <Icon
                            className={cn('size-3.5', direction === 'none' && 'opacity-40')}
                            strokeWidth={2}
                          />
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`} className="hover:bg-transparent">
                  {Array.from({ length: columnCount }).map((__, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton className="h-4 w-full max-w-[12rem]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columnCount} className="h-40 text-center align-middle">
                  {empty}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          // Enter/Space on the row itself activates it. Guarded
                          // to the row so a nested control (checkbox, action
                          // button) keeps its own keys and does not double-fire.
                          if (
                            event.target === event.currentTarget &&
                            (event.key === 'Enter' || event.key === ' ')
                          ) {
                            event.preventDefault();
                            onRowClick(row.original);
                          }
                        }
                      : undefined
                  }
                  className={cn(
                    onRowClick &&
                      'cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {/* A cell grows to three lines and is then cut with an
                          ellipsis, so one long value cannot stretch the row down
                          the page. line-clamp needs a block of its own — a `td`
                          cannot clamp itself. */}
                      <div className="line-clamp-3">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}

            {loadingMore && !loading && (
              <TableRow className="hover:bg-transparent">
                {Array.from({ length: columnCount }).map((_, index) => (
                  <TableCell key={index}>
                    <Skeleton className="h-4 w-full max-w-[12rem]" />
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {paginated && !loading && showsPagination(table, totalRows) && (
        <div className="border-t px-4 py-3">
          <DataTablePagination table={table} totalRows={totalRows} />
        </div>
      )}

      {footer && <div className="border-t px-4 py-3">{footer}</div>}
    </div>
  );
}

/** Generic components lose their type parameter through React.memo, so this is a cast. */
export const DataTable = React.memo(DataTableComponent) as typeof DataTableComponent;
